import { useSignal } from '@preact/signals';
import { useLocation } from 'preact-iso';

import { Button, Notice, TextField } from '@amumin/ui';

import { api, errorMessage, isApiError } from '../api.js';
import { AuthLayout, tokenFromUrl } from '../components/AuthLayout.js';
import { validatePassword } from '../lib/validation.js';
import { messages } from '../state/session.js';

/** Nieuw wachtwoord na de resetlink (token in het fragment; POST vanaf de knop). */
export function ResetConfirmPage() {
  const { query, route } = useLocation();
  const m = messages.value;
  const token = tokenFromUrl(query);
  const password = useSignal('');
  const repeat = useSignal('');
  const errors = useSignal<{ password?: string | undefined; repeat?: string | undefined }>({});
  const error = useSignal<string | undefined>(undefined);
  const busy = useSignal(false);

  const submit = async () => {
    if (!token) return;
    const pw = validatePassword(password.value);
    errors.value = {
      password: pw ? m.web[pw] : undefined,
      repeat: !pw && repeat.value !== password.value ? m.web.passwordMismatch : undefined,
    };
    if (errors.value.password || errors.value.repeat) return;
    busy.value = true;
    error.value = undefined;
    try {
      await api.app.auth.resetConfirm(token, password.value);
      route('/login?reset=1', true);
    } catch (e) {
      error.value =
        isApiError(e, 400) || isApiError(e, 404) ? m.web.resetInvalid : errorMessage(e, m);
    } finally {
      busy.value = false;
    }
  };

  return (
    <AuthLayout title={m.web.resetConfirmTitle}>
      {!token ? <Notice kind="error">{m.web.tokenMissing}</Notice> : null}
      {error.value ? <Notice kind="error">{error.value}</Notice> : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <TextField
          id="password"
          label={m.web.newPassword}
          type="password"
          value={password.value}
          onInput={(v) => (password.value = v)}
          autoComplete="new-password"
          error={errors.value.password}
          hint={m.web.passwordHint}
          disabled={!token}
          required
        />
        <TextField
          id="password-repeat"
          label={m.web.passwordRepeat}
          type="password"
          value={repeat.value}
          onInput={(v) => (repeat.value = v)}
          autoComplete="new-password"
          error={errors.value.repeat}
          disabled={!token}
          required
        />
        <Button
          name="reset-confirm"
          type="submit"
          variant="primary"
          block
          busy={busy.value}
          disabled={!token}
        >
          {m.web.resetConfirmButton}
        </Button>
      </form>
      <p class="auth-links">
        <a href="/login">{m.web.toLogin}</a>
      </p>
    </AuthLayout>
  );
}
